import {
  Dialog,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  Button,
  MenuItem,
  Grid,
  Box,
} from '@mui/material';

import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, useForm } from 'react-hook-form';
import dayjs from 'dayjs';

import { useProjects } from '../../context/ProjectContext';
import DateInputController from '../../components/DateInputController';
import { usePlan } from '../../context/WorkPlanContext';

export const workPlanSchema = z.object({
  project: z.string().min(1, 'Project is required'),
  task_name: z.string().min(1, 'Task Name is required'),
  start_day: z.string().min(1, 'Start Date is required'),
  end_day: z.string().min(1, 'End Date is required'),
  work_time_per_day: z.number().nonnegative().max(24, 'Max hour is 24'),
});

const WorkPlanDialog = ({ isOpen, onClose, selectedWorkLogged }) => {
  const { addPlan, updatePlan } = usePlan();
  const { projects } = useProjects();

  const {
    control,
    formState: { errors },
    setValue,
    reset,
    clearErrors,
    handleSubmit,
  } = useForm({
    resolver: zodResolver(workPlanSchema),
    values: {
      project: selectedWorkLogged?.project._id || '',
      task_name: selectedWorkLogged?.content || '',
      start_day: selectedWorkLogged?.start_day
        ? dayjs(selectedWorkLogged?.start_day).format('YYYY-MM-DD')
        : '',
      end_day: selectedWorkLogged?.end_day
        ? dayjs(selectedWorkLogged?.end_day).format('YYYY-MM-DD')
        : '',
      work_time_per_day: selectedWorkLogged?.work_time_per_day || '',
    },
  });

  const handleSubmitWorkLog = (payload) => {
    const data = {
      project: payload.project, // Ensure to include the event ID
      content: payload.task_name,
      start_day: payload.start_day,
      end_day: payload.end_day,
      work_time_per_day: payload.work_time_per_day,
    };
    if (selectedWorkLogged) {
      data.id = selectedWorkLogged.id;
    }

    selectedWorkLogged ? updatePlan(data) : addPlan(data);
    onClose();
  };

  const handleCloseModal = () => {
    reset({
      project: '',
      task_name: '',
      start_day: '',
      end_day: '',
      work_time_per_day: '',
    });
    clearErrors();
    onClose();
  };

  const renderInput = (name, label, type = 'text') => (
    <Controller
      name={name}
      control={control}
      render={({ field }) => (
        <TextField
          {...field}
          type={type}
          label={label}
          variant="outlined"
          onChange={(evt) => {
            const val = evt.target.value;
            setValue(name, type === 'number' ? Number(val) : val);
          }}
          fullWidth
          error={!!errors[name]}
          helperText={
            errors[name]
              ? errors[name].message === 'Expected number, received string'
                ? `${label} is required`
                : errors[name].message
              : ''
          }
        />
      )}
    />
  );

  return (
    <Dialog open={isOpen} onClose={handleCloseModal}>
      <DialogTitle>
        {selectedWorkLogged ? 'Edit Task' : 'Add Log Task'}
      </DialogTitle>
      <DialogContent>
        <DialogContentText mb={1}>
          {selectedWorkLogged
            ? 'Edit the details of the logged task.'
            : 'Enter the details for the task.'}
        </DialogContentText>

        <form onSubmit={handleSubmit(handleSubmitWorkLog)} noValidate>
          <Grid container spacing={2} mt={1}>
            <Grid item xs={12}>
              <Controller
                name="project"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    select
                    label="Project"
                    variant="outlined"
                    fullWidth
                    error={!!errors.project}
                    helperText={errors.project ? errors.project.message : ''}>
                    {projects.map((item) => (
                      <MenuItem value={item.id} key={item.id}>
                        {item.name}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
            </Grid>
            <Grid item xs={12}>
              {renderInput('task_name', 'Task Name')}
            </Grid>

            <DateInputController
              control={control}
              error={errors.start_day}
              name="start_day"
              label="Start Day"
              setValue={setValue}
              clearErrors={clearErrors}
            />
            <DateInputController
              control={control}
              error={errors.end_day}
              name="end_day"
              label="End Day"
              setValue={setValue}
              clearErrors={clearErrors}
            />

            <Grid item xs={12}>
              {renderInput('work_time_per_day', 'Working Time', 'number')}
            </Grid>

            {/* Submit Button */}
            <Grid item xs={12}>
              <Box display="flex" justifyContent="flex-end" gap={1}>
                <Button onClick={handleCloseModal}>Cancel</Button>
                <Button type="submit" color="primary" variant="contained">
                  {selectedWorkLogged ? 'Save' : 'Add New Log'}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default WorkPlanDialog;
